import { UseMutateAsyncFunction, useMutation } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { PublishPostRequestParams, queryClient } from 'api';
import { API_HANDLERS } from 'api/apiHandlers';
import { alert } from 'components';
import * as ERROR_CODES from 'constants/errorCodes';
import { ApiErrors } from 'types';

interface UsePublishPostMutationReturn {
  publishPostMutation: UseMutateAsyncFunction<
    AxiosResponse<unknown, unknown>,
    unknown,
    PublishPostRequestParams,
    unknown
  >;
  isLoading: boolean;
  isSuccess: boolean;
}

interface UsePublishPostMutationProps {
  successText: string;
  setIsSuccess?: Dispatch<SetStateAction<boolean>>;
  invalidateQueries?: string[];
}

export function usePublishPostMutation({
  successText,
  setIsSuccess,
  invalidateQueries,
}: UsePublishPostMutationProps): UsePublishPostMutationReturn {
  const { t } = useTranslation();
  const {
    mutateAsync: publishPostMutation,
    isLoading,
    isSuccess,
  } = useMutation(API_HANDLERS.POST.PUBLISH, {
    onSuccess: () => {
      setIsSuccess && setIsSuccess(true);
      alert.success(successText);

      if (invalidateQueries) {
        queryClient.invalidateQueries(invalidateQueries);
      }
    },
    onError: (error: AxiosError<ApiErrors>) => {
      if (error.response?.data.errors[0].code === ERROR_CODES.ASSIGNEE_REQUEST) {
        alert.error(t('toasts.codes.postAssigneeError'));
      }
    },
  });

  return {
    publishPostMutation,
    isLoading,
    isSuccess,
  };
}
